'use client'

export type GppSignalStatus = 'not ready' | 'ready'

export type GppPingData = {
    applicableSections: number[]
    cmpStatus: string
    gppString: string
    gppVersion: string
    signalStatus: GppSignalStatus
}

export type GppEvent = {
    eventName: string
    listenerId: number
    pingData: GppPingData
}

export type GppCallback = (data: GppPingData | GppEvent, success: boolean) => void
export type GppApi = (command: string, callback: GppCallback, parameter?: unknown, version?: string) => void

type GppTarget = Pick<Window, '__gpp'>

export type GppReadyObserver = {
    ready: Promise<GppPingData>
    recheck: () => void
}

const readinessObservers = new WeakMap<object, GppReadyObserver>()

function pingDataFromCallback(data: GppPingData | GppEvent): GppPingData {
    return 'pingData' in data ? data.pingData : data
}

export function resolveGppReady(api: GppApi): Promise<GppPingData> {
    return createGppReadyObserver(api).ready
}

function createGppReadyObserver(api: GppApi | undefined): GppReadyObserver {
    if (!api) {
        return {
            ready: new Promise<GppPingData>(() => undefined),
            recheck: () => undefined,
        }
    }

    let resolveReady: (pingData: GppPingData) => void
    let resolved = false
    const listenerIds = new Set<number>()
    const removedListenerIds = new Set<number>()
    const ready = new Promise<GppPingData>((resolve) => {
        resolveReady = resolve
    })

    const removeListener = (listenerId: number) => {
        if (removedListenerIds.has(listenerId)) {
            return
        }
        removedListenerIds.add(listenerId)
        api('removeEventListener', () => undefined, listenerId)
    }

    const resolveIfReady = (data: GppPingData | GppEvent, success: boolean) => {
        if (!success) {
            return
        }

        if ('listenerId' in data) {
            listenerIds.add(data.listenerId)
            if (resolved) {
                removeListener(data.listenerId)
            }
        }

        if (resolved) {
            return
        }

        const pingData = pingDataFromCallback(data)
        if (pingData.signalStatus !== 'ready') {
            return
        }

        resolved = true
        listenerIds.forEach(removeListener)
        resolveReady(pingData)
    }

    const readCurrentState = () => {
        api('ping', resolveIfReady)
    }
    const recheck = () => {
        if (!resolved) {
            readCurrentState()
        }
    }

    api('addEventListener', resolveIfReady)
    readCurrentState()

    return { ready, recheck }
}

export function observeGppReady(target: GppTarget = window): GppReadyObserver {
    const cachedObserver = readinessObservers.get(target)
    if (cachedObserver) {
        return cachedObserver
    }

    const observer = createGppReadyObserver(target.__gpp)
    readinessObservers.set(target, observer)
    return observer
}

export function waitForGppReady(target: GppTarget = window): Promise<GppPingData> {
    return observeGppReady(target).ready
}
